import React, { useState, useEffect } from "react";
import {
  Typography,
  TextField,
  Button,
  Box,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Link,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useRole } from "../contexts/RoleContext";
import { YandexIcon, GoogleIcon } from "../components/Common/OAuthIcons";
import { isBrigadeDomain } from "../utils/domain";

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login: loginUser, isAuthenticated, user } = useAuth();
  const { isLoading: roleLoading } = useRole();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // OAuth на всех доменах
  const showOAuth = true;

  useEffect(() => {
    if (isAuthenticated && !roleLoading) {
      // Route by the account's primary role, not the in-app switched role:
      // a stale selected_role_* must never send an admin to the FSM board.
      const primaryRole =
        (user as any)?.default_role || (user as any)?.role;
      if (primaryRole === "admin" || primaryRole === "super_admin") {
        navigate("/admin", { replace: true });
      } else if (isBrigadeDomain()) {
        // Brigade domain: always go to FSM
        navigate("/fsm/board", { replace: true });
      } else {
        // Help domain: always go to home
        navigate("/home", { replace: true });
      }
    }
  }, [isAuthenticated, roleLoading, user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const result = await loginUser(
        formData.email,
        formData.password,
        formData.rememberMe,
      );

      if (!result.success) {
        setError(result.error || t("invalid_credentials"));
        setLoading(false);
      }
      // On success: keep loading=true, AuthContext+RolesContext will trigger
      // the useEffect above to redirect based on the actual role
    } catch (err) {
      setError(t("invalid_credentials"));
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Пост-OAuth редирект.
  //
  // Нельзя использовать "/": на dispatcherpro.ru location / отдаёт маркетинговый
  // сайт (Next.js), а не SPA — после успешного входа пользователь попадал на
  // лендинг вместо приложения (nginx/conf.d/dispatcherpro.conf, "location /").
  // "/landing" — SPA-роут с RoleBasedRedirect, который есть в nginx-allowlist
  // обоих доменов и раскладывает пользователя по роли.
  const postAuthPath = "/landing";

  const startOAuth = (provider: "yandex" | "google") => {
    const redirectUrl = encodeURIComponent(window.location.origin + postAuthPath);
    window.location.href = `/api/v1/auth/${provider}?redirect=${redirectUrl}`;
  };

  const handleYandexLogin = () => startOAuth("yandex");

  const handleGoogleLogin = () => startOAuth("google");

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundImage: `
          linear-gradient(135deg, rgba(25, 118, 210, 0.85) 0%, rgba(33, 150, 243, 0.85) 100%),
          url("/background5.webp")
        `,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Card sx={{ minWidth: 400 }}>
        <CardContent>
          <Typography variant="h5" align="center" gutterBottom>
            {t("login")}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t("email")}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              error={!!error && !formData.email}
              helperText={error && !formData.email ? t("required_field") : ""}
              autoComplete="email"
            />

            <TextField
              fullWidth
              label={t("password")}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              required
              disabled={loading}
              error={!!error && !formData.password}
              helperText={
                error && !formData.password ? t("required_field") : ""
              }
              onKeyDown={(e) => {
                // Enter в поле пароля отправляет форму
                if (e.key === "Enter") {
                  e.preventDefault();
                  handleSubmit(e as any);
                }
              }}
              autoComplete="current-password"
            />

            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleChange}
                  disabled={loading}
                  color="primary"
                />
              }
              label={t("remember_me")}
              sx={{ mt: 1 }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 2 }}
              disabled={loading}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t("logging_in")}...
                </>
              ) : (
                t("login")
              )}
            </Button>
          </form>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {t("or")} {t("continue_with")}
            </Typography>
          </Divider>

          {showOAuth && (
          <>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleYandexLogin}
            disabled={loading}
            startIcon={<YandexIcon />}
            sx={{ mt: 1 }}
          >
            {t("login_with_yandex")}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={handleGoogleLogin}
            disabled={loading}
            startIcon={<GoogleIcon />}
            sx={{ mt: 1 }}
          >
            {t("login_with_google")}
          </Button>
          </>
          )}

          <Box sx={{ mt: 2, textAlign: "center" }}>
            <Typography variant="body2" color="textSecondary">
              {t("dont_have_account")}{" "}
              <Link component="a" href="/register" underline="hover">
                {t("register")}
              </Link>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
